import dayjs from "dayjs"

const DATE_FORMAT = "DD/MM/YYYY"

const formatDate = (value) => (value ? dayjs(value).format(DATE_FORMAT) : "-")

const sortByDate = (rows, field = "DataReferencia") => [...rows].sort((first, second) => {
    const firstDate = dayjs(first?.[field])
    const secondDate = dayjs(second?.[field])
    return secondDate.valueOf() - firstDate.valueOf()
})

const getRows = (response) => response?.body?.results ?? []

const historyFields = {
    rating: [
        { name: "DataReferencia", label: "Data referência", type: "date", required: true },
        { name: "DataValidade", label: "Data validade", type: "date", required: true },
        { name: "Fonte", label: "Fonte", required: true },
        { name: "ScoreRating", label: "Score rating", type: "number", required: true },
        { name: "Rating", label: "Rating", required: true },
        { name: "Sinal", label: "Sinal" },
        { name: "Perspectiva", label: "Perspectiva" },
        { name: "AnaliseCreditoDevedor", label: "Análise de crédito do devedor", multiline: true },
        { name: "Observacao", label: "Observação", multiline: true, maxLength: 300 },
    ],
    event: [
        { name: "DataReferencia", label: "Data referência", type: "date", required: true },
        { name: "EventoCreditoDevedor", label: "Evento de crédito", multiline: true, required: true },
        { name: "ScoreEvento", label: "Score evento", type: "number" },
    ],
}

const makeEditableHistory = ({ fields, createDefaults, request }) => ({
    fields,
    createDefaults,
    request,
})

export const COMPANY_HISTORY_CONFIG = {
    rating: {
        title: "Histórico Rating Devedor",
        emptyText: "Esse devedor não possui rating.",
        getIdentifier: (company) => company?.CPFCNPJ,
        async load({ emissaoService, identifier }) {
            const response = await emissaoService.ratingDevedor("get", `?CPFCNPJ=${encodeURIComponent(identifier)}`)
            return sortByDate(getRows(response)).map((item) => ({ ...item, id: item.ID }))
        },
        editable: makeEditableHistory({
            fields: historyFields.rating,
            createDefaults: (company) => ({
                CPFCNPJ: company?.CPFCNPJ,
                DataReferencia: dayjs().format("YYYY-MM-DD"),
                DataValidade: "",
                Devedor: company?.RazaoSocial ?? company?.NomeFantasia ?? "N/A",
                Setor: company?.Setor ?? "N/A",
                SubSetor: company?.SubSetor ?? "N/A",
                Fonte: "",
                ScoreRating: "",
                Rating: "",
            }),
            request: ({ emissaoService, method, row }) => emissaoService.ratingDevedor(method, method === "post" ? row : row.ID, method === "patch" ? row : null),
        }),
        columns: [
            { field: "DataReferencia", headerName: "Data referência", type: "date" },
            { field: "DataValidade", headerName: "Data validade", type: "date" },
            { field: "Fonte", headerName: "Fonte" },
            { field: "ScoreRating", headerName: "Score rating" },
            { field: "Rating", headerName: "Rating" },
            { field: "Sinal", headerName: "Sinal" },
            { field: "Perspectiva", headerName: "Perspectiva" },
            { field: "Observacao", headerName: "Observação", minWidth: 240 },
            { field: "AnaliseCreditoDevedor", headerName: "Análise de crédito", minWidth: 280 },
        ],
    },
    event: {
        title: "Histórico Evento de Crédito Devedor",
        emptyText: "Esse devedor não possui eventos de crédito.",
        getIdentifier: (company) => company?.CPFCNPJ,
        async load({ emissaoService, identifier }) {
            const response = await emissaoService.eventoCreditoDevedor("get", `?CPFCNPJ=${encodeURIComponent(identifier)}`)
            return sortByDate(getRows(response)).map((item) => ({ ...item, id: item.ID }))
        },
        editable: makeEditableHistory({
            fields: historyFields.event,
            createDefaults: (company) => ({
                CPFCNPJ: company?.CPFCNPJ,
                DataReferencia: dayjs().format("YYYY-MM-DD"),
            }),
            request: ({ emissaoService, method, row }) => emissaoService.eventoCreditoDevedor(method, method === "post" ? row : row.ID, method === "patch" ? row : null),
        }),
        columns: [
            { field: "DataReferencia", headerName: "Data referência", type: "date" },
            { field: "EventoCreditoDevedor", headerName: "Evento de crédito", minWidth: 420 },
            { field: "ScoreEvento", headerName: "Score evento" },
            { field: "CriadoEm", headerName: "Criado em", type: "date", adminOnly: true },
            { field: "AtualizadoEm", headerName: "Atualizado em", type: "date", adminOnly: true },
        ],
    },
    fact: {
        title: "Histórico Fato Relevante",
        emptyText: "Não há fato relevante disponível.",
        getIdentifier: (company) => company?.CodigoCVM,
        async load({ datalakeService, identifier }) {
            const response = await datalakeService.loadFatoRelevante(identifier)
            return sortByDate(getRows(response)).map((item) => ({ ...item, id: item.ID }))
        },
        columns: [
            { field: "DataReferencia", headerName: "Data referência", type: "date" },
            { field: "DataEntrega", headerName: "Data entrega", type: "date" },
            { field: "Categoria", headerName: "Categoria" },
            { field: "Assunto", headerName: "Assunto", minWidth: 280 },
            { field: "Resumo", headerName: "Resumo", minWidth: 420 },
            { field: "Link", headerName: "Documento", type: "link" },
        ],
    },
    probabilityDefault: {
        title: "Histórico Probabilidade de Default",
        emptyText: "Não há probabilidade de default disponível.",
        details: {
            title: "Detalhes da probabilidade de default",
            fields: [
                { field: "DataReferencia", label: "Data referência", type: "date" },
                { field: "PD", label: "PD" },
                { field: "Ticker", label: "Ticker" },
                { field: "Fonte", label: "Fonte" },
                { field: "EWMA", label: "EWMA" },
                { field: "FonteRating", label: "Fonte do rating" },
                { field: "RatingFinal", label: "Rating final" },
                { field: "MesBaseRating", label: "Mês-base do rating" },
            ],
        },
        getIdentifier: (company) => company?.RaizCNPJ,
        async load({ datalakeService, identifier }) {
            const response = await datalakeService.loadPD(identifier)
            return sortByDate(getRows(response)).map((item) => ({ ...item, id: item.ID ?? `${item.DataReferencia}-${item.PD}` }))
        },
        columns: [
            { field: "DataReferencia", headerName: "Data referência", type: "date" },
            { field: "PD", headerName: "PD" },
        ],
    },
}

export const formatHistoryValue = (value, type) => {
    if (value === null || value === undefined || value === "") return "-"
    if (type === "date") return formatDate(value)
    return value
}
